import React, { useEffect, useState, useRef } from "react";
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faTrash } from '@fortawesome/free-solid-svg-icons';


const EditPost = () => {
  const [content, setContent] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [tokenId, setTokenId] = useState("");
  const fileInputRef = useRef(null);

  const params = new URLSearchParams(window.location.search);
  const contentID = params.get("contentID");
  const userEmail = params.get("email");
  const AccountType = params.get('AccountType');

  useEffect(() => {
    if (!contentID || !userEmail) return;

    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/getPostDetails?email=${userEmail}&contentID=${contentID}`)
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        // ✅ Safely decode content - handle cases where content might already be decoded or malformed
        let decodedContent = '';
        if (data.content) {
          try {
            // Try to decode - if it fails, content might already be decoded
            decodedContent = decodeURIComponent(data.content);
          } catch (decodeError) {
            // If decoding fails, use content as-is (it's likely already decoded)
            console.warn('Content decode failed, using as-is:', decodeError);
            decodedContent = data.content;
          }
        }
        
        setContent(decodedContent);
        setEmail(userEmail);
        setAccessToken(data.accessToken);
        setTokenId(data.tokenId);
        if (data.imageUrl) {
          setImageUrl(data.imageUrl);
        }
        setLoading(false); // ✅ Stop loader after successful fetch
      })
      .catch(err => {
        console.error("Error loading post:", err);
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: 'Error loading post',
          text: err.message || 'Failed to load post details. Please try again.',
          showConfirmButton: false,
          timer: 3000
        });
        setLoading(false); // ✅ Stop loader after successful fetch
      });
  }, [contentID, userEmail]);

 
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); // Start loader
  
    const formData = new FormData();
    
    // Handle image: if a new file is selected, upload it; otherwise, send the existing image URL
    if (selectedFile) {
      // New file selected - upload it
      formData.append('newImage', selectedFile);
    } else if (imageUrl && imageUrl.trim() !== '') {
      // No new file, but there's an existing image URL
      // Ensure it's a full URL (backend expects URLs starting with "http")
      const imageUrlToSend = imageUrl.startsWith('http') 
        ? imageUrl 
        : `${process.env.REACT_APP_API_BASE_URL}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
      formData.append('newImage', imageUrlToSend);
    }
    // If neither selectedFile nor imageUrl exists, don't append 'newImage' (post without image)
  
    formData.append('email', email);
    formData.append('content', content);
    formData.append('contentID', contentID);
    formData.append('AccessToken', accessToken);
    formData.append('Tokenid', tokenId);
    formData.append('AccountType', AccountType);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/postToLinkedInedit`, {
        method: "POST",
        body: formData,
      });
  
      const result = await res.json();
      setLoading(false);
  
      if (res.ok) {
        // Check if image was skipped
        if (result.imageSkipped) {
          Swal.fire({
            position: 'center',
            icon: 'warning',
            title: 'Posted Successfully (Without Image)',
            text: 'Your post was published, but the image was skipped because the image URL was expired or inaccessible.',
            showConfirmButton: true,
            confirmButtonText: 'OK',
            confirmButtonColor: '#2e3192',
            timer: 8000,
          }).then(() => {
            window.location.href = "https://www.linkedin.com/feed/";
          });
        } else {
          Swal.fire({
            position: 'center',
            icon: 'success',
            title: 'Posted Successfully to LinkedIn',
            showConfirmButton: false,
            timer: 6000,
          });
          window.location.href = "https://www.linkedin.com/feed/";
        }
      } else {
        // Check if it's a limit exceeded error (402)
        if (res.status === 402) {
          Swal.fire({
            position: 'center',
            icon: 'warning',
            title: 'Knack Feeds Limit Reached',
            text: `You have reached your limit for Knack Feeds. Please try again tomorrow.`,
            showConfirmButton: true,
            confirmButtonText: 'OK',
            confirmButtonColor: '#2e3192',
            showCancelButton: false
          });
        } else {
          const errorMessage = result.message || result.error || 'Something went wrong. Try again.';
          // Check if it's an image-related error
          const isImageError = errorMessage.toLowerCase().includes('image') || 
                               errorMessage.toLowerCase().includes('upload') ||
                               errorMessage.toLowerCase().includes('media');
          
          Swal.fire({
            icon: 'error',
            title: 'Failed to post',
            text: errorMessage,
            ...(isImageError && {
              html: `
                <div style="text-align: left;">
                  <p><strong>Image posting failed:</strong></p>
                  <p>${errorMessage}</p>
                  <p style="margin-top: 10px;"><strong>Try:</strong></p>
                  <ol style="margin-left: 20px; padding-left: 10px;">
                    <li>Remove the current image</li>
                    <li>Upload a new image from your device</li>
                    <li>Or post without an image</li>
                  </ol>
                </div>
              `
            })
          });
        }
      }
    } catch (error) {
      setLoading(false);
      
      // Check if it's a limit exceeded error (402)
      if (error.response?.status === 402) {
        Swal.fire({
          position: 'center',
          icon: 'warning',
          title: 'Knack Feeds Limit Reached',
          text: `You have reached your limit for Knack Feeds. Please try again tomorrow.`,
          showConfirmButton: true,
          confirmButtonText: 'OK',
          confirmButtonColor: '#2e3192',
          showCancelButton: false
        });
      } else {
        const errorMessage = error.response?.data?.message || 
                            error.response?.data?.error || 
                            error.message || 
                            'Something went wrong while posting.';
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage,
        });
      }
      console.error("Error:", error);
    }
  };
  

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa', padding: '20px 0' }}>
    {loading ? (
      <div className="spinner"></div>
  ) : (
    <div style={{ 
      maxWidth: "900px", 
      margin: "0 auto", 
      padding: "30px 20px"
    }}>
      <div style={{
        background: '#FFFFFF',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        padding: '32px',
        marginBottom: '20px'
      }}>
        <h2 style={{ 
          margin: '0 0 24px 0', 
          fontSize: '28px', 
          fontWeight: '700',
          color: '#2e3192'
        }}>
          Edit Your Post
        </h2>
        
        <form onSubmit={handleSubmit}>
          {/* Image Preview Section */}
          {imageUrl && (
            <div style={{ 
              marginBottom: "24px",
              padding: '20px',
              background: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e9ecef'
            }}>
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px'
              }}>
                <img 
                  src={imageUrl} 
                  alt="Post" 
                  style={{ 
                    maxWidth: "100%",
                    maxHeight: "400px",
                    borderRadius: "8px", 
                    display: "block",
                    objectFit: 'contain',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }} 
                />
                <button 
                  type="button" 
                  onClick={() => {
                    setImageUrl("");
                    setSelectedFile(null);
                  }}
                  style={{
                    background: '#dc3545',
                    border: 'none',
                    color: 'white',
                    padding: '10px 20px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#c82333';
                    e.target.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#dc3545';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} style={{ fontSize: '14px' }} />
                  Remove Image
                </button>
              </div>
            </div>
          )}

          {/* Upload Image Button */}
          {!imageUrl && (
            <div style={{ marginBottom: "24px" }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setSelectedFile(file);
                    setImageUrl(URL.createObjectURL(file));
                  }
                }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                title="Upload Image"
                style={{
                  fontSize: '14px',
                  borderRadius: '8px',
                  border: '2px dashed #2e3192',
                  background: '#f8f9ff',
                  padding: '12px 20px',
                  color: '#2e3192',
                  fontWeight: '600',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  width: '100%'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#e8eaff';
                  e.target.style.borderColor = '#1a237e';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#f8f9ff';
                  e.target.style.borderColor = '#2e3192';
                }}
              >
                <FontAwesomeIcon icon={faUpload} />
                Upload Image
              </button>
            </div>
          )}

          {/* Content Textarea */}
          <div style={{ marginBottom: "24px" }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#495057'
            }}>
              Post Content
            </label>
            <textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder="Write your post content here..."
              style={{
                fontSize: '16px',
                lineHeight: '1.6',
                borderRadius: '8px',
                border: '1px solid #dee2e6',
                background: '#FFFFFF',
                minHeight: '400px',
                maxHeight: '600px',
                width: "100%",
                padding: '16px',
                fontFamily: 'inherit',
                resize: 'vertical',
                transition: 'border-color 0.3s ease',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#2e3192';
                e.target.style.boxShadow = '0 0 0 3px rgba(46, 49, 146, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#dee2e6';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Submit Button */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            gap: '12px',
            paddingTop: '16px',
            borderTop: '1px solid #e9ecef'
          }}>
            <button 
              type="submit" 
              style={{
                background: '#2e3192',
                border: 'none',
                color: 'white',
                padding: '12px 32px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 2px 4px rgba(46, 49, 146, 0.2)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#1a237e';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 12px rgba(46, 49, 146, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#2e3192';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 4px rgba(46, 49, 146, 0.2)';
              }}
            >
              {(AccountType === 'Page' || AccountType === 'page') 
                ? 'Post to LinkedIn, Facebook & Instagram'
                : 'Post to LinkedIn'}
            </button>
          </div>
        </form>
      </div>
    </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .edit-post-container {
            padding: 16px !important;
          }
          .edit-post-card {
            padding: 24px 16px !important;
          }
        }
      `}</style>
      </div>
  );
};



export default EditPost;
